import React, { Component, useState, useEffect, createRef } from 'react';
import Pagination from 'components/v2/PaginationV2';
import { find, isEmpty, isNull, uniq, cloneDeep } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { LicenseManager } from 'ag-grid-enterprise';
import { titleize } from 'underscore.string';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import ColumnsSetStore from 'stores/ColumnsSetStore';
import ColumnsWebAPIUtils from 'utils/ColumnsWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import FilterActionCreators from 'actions/FilterActionCreators';
import {
  getShipmentsByStream,
  getShipmentsCount,
  getUVCsvExport,
  createReport,
  updateReport,
  getReport,
  deleteReport,
  setFavoriteReport,
} from 'utils/UnifiedViewWebAPIUtils';
import FiltersStore from 'stores/FiltersStore';
import { getFilter } from 'utils/FiltersWebAPIUtils';
import OrderModule from 'components/modules/orders/Module';
import ShipmentModule from 'components/modules/shipments/Shipment';
import CargomaticModal from 'components/v2/modal';
import { useTranslation, withTranslation } from 'react-i18next';
import { getLocaleText } from 'utils/localization/agGridLocale';
import { withLocaleContext } from 'providers/locale-settings-context';
import USStates from 'constants/us-states';
import { isProd, isUS } from 'helpers/helper_functions';
import { sluggify, firstLetterTitlization } from 'utils/localization/utils';
import { Menu } from '@headlessui/react';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import Breadcrumbs from 'components/v2/Breadcrumbs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cargomatic/ui';
import Favorite from './Favorite';
import Layout from './Layout';

import { shipmentHasEquipment, getStopGroupsArray } from './utils';

import BulkActions from './BulkActions';
import {
  getStopPrefixAndField,
  getDrayageColumns,
  getShorthaulColumns,
} from './columns';
import SaveFilterModal from './SaveFilterModal';
import { IMPORT, DOMESTIC } from '../../../config/order-types';
import {
  STANDARD_REFERENCE_NUMBERS,
  SIDEBAR_STATUSES,
  TIME_SELECTIONS,
} from './constants';
import EditableColumn from './customCellRenderers/EditableColumn';

import {
  tagFormatter,
  statusFormatter,
  portIntegrationsFormatter,
  orderIDRenderer,
  shipmentIDRenderer,
  marginFormatter,
  deliveryOrderFormatter,
  checkedFormatter,
  lineSeparatedStringFormatter,
} from './formatters';
import TextFilterWithExclude from './customFilters/TextFilterWithExclude';
import TextFilter from './customFilters/TextFilter';
import SaveModal from './SaveModal';
import DeleteReportModal from './DeleteReportModal';
import {
  getModelParams,
  loadColumnState,
  transformFilterModelQuery,
  getInitialDateRange,
  getMinValidDate,
} from './transformers';
import FilterChips from './FilterChips';
import { withPageContext } from './PageContext';
import { withGroups } from '../../../hooks/unifiedview/useReports';
import { ColumnCategoriesDropdown } from './ColumnCategoriesDropdown';

const getOptions = (t) => [
  {
    name: t('DRAYAGE'),
    value: 'drayage',
  },
  {
    name: t('SHORTHAUL'),
    value: 'shorthaul',
  },
];

LicenseManager.setLicenseKey(`${AG_GRID_LICENSE}`);

const ARRIVED_ORDER_TYPES = [IMPORT, DOMESTIC];
const POLLING_FREQUENCY = 60000; // 1 min
const INACTIVE_SHIPMENT_STATUS = ['Job Closed', 'Canceled'];

const DEFAULT_CREATED_AT_SORT = {
  colId: 'created_at',
  sort: 'desc',
  sortIndex: 99,
};

const createdAtDefaultFilterModel = {
  created_at: {
    ...getInitialDateRange(),
    filterType: 'date',
    type: 'inRange',
  },
};

const perPageOptions = [100, 500];

const drayageFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'drayage',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'rail',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const shorthaulFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'shorthaul',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'longhaul',
      filterType: 'text',
    },
    condition3: {
      type: 'equals',
      filter: 'dayrate',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const PerPage = (props) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="div-db370b9d"
      className="flex items-center my-auto mr-4 space-x-2"
    >
      <label data-testid="label-122f0293" className="form-label">
        {t('PER_PAGE')}
      </label>
      <select
        data-testid="select-21575e42"
        onChange={props.onChange}
        value={props.selectedValue}
        className="form-select"
      >
        {props.options.map((item, index) => {
          return (
            <option
              data-testid={`option-e3a17c30-${index}`}
              key={item}
              value={item}
            >
              {item}
            </option>
          );
        })}
      </select>
    </div>
  );
};

PerPage.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
};

const sidebarParams = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: false,
        suppressColumnSelectAll: false,
        suppressColumnExpandAll: false,
        suppressSyncLayoutWithGrid: true,
      },
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      toolPanelParams: {
        suppressExpandAll: false,
        suppressFilterSearch: false,
      },
    },
  ],
};
const gridOpts = {
  defaultColDef: {
    enableRowGroup: true,
    enablePivot: false,
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    cellRenderer: EditableColumn,
  },
  rowDragManaged: true,
  copyHeadersToClipboard: true,
  suppressColumnVirtualisation: true,
  suppressScrollOnNewData: true,
};

const FilterListComponent = (props) => {
  return (
    <div data-testid="div-deb3fcfd">
      <fieldset data-testid="fieldset-27f5ee24">
        <span data-testid="span-1a7741ac" className="form-label">
          {props.name}
        </span>
        {props.filters.length === 0 && (
          <div data-testid="div-11dd6ab9" className="text-sm text-grey-dark">
            No Filters Found...
          </div>
        )}
        <div data-testid="div-dcce3944" className="flex">
          {props.filters.map((filter, i) => {
            return (
              <div data-testid={`div-18634715-${i}`} className="mr-2" key={i}>
                <span
                  data-testid={`span-61cabd6a-${i}`}
                  className="mr-2 text-sm text-grey-darker"
                >
                  {filter.name}
                </span>
                <input
                  data-testid={`input-07f6fb46-${i}`}
                  key={filter._id}
                  type="checkbox"
                  value=""
                  checked={props.selectedFilter._id === filter._id}
                  onChange={(e) => {
                    props.onFilterChange(
                      filter,
                      props.selectedFilter,
                      props.filterSelected,
                      e.target.value,
                    );
                  }}
                />
              </div>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
};

FilterListComponent.propTypes = {
  name: PropTypes.string,
  filters: PropTypes.array,
  selectedFilter: PropTypes.object,
};

// A preliminary component for redirecting users from areas they do not have access too.
const CountDown = () => {
  const [count, setCount] = useState(3);

  useEffect(() => {
    const interval = setInterval(() => {
      if (count > 0) {
        setCount(count - 1);
      }
    }, 1000);

    // Cleanup the interval when the component unmounts
    return () => clearInterval(interval);
  }, [count]);

  if (count === 0) {
    window.location.href = '/';
  }

  return (
    <div
      data-testid="div-9b2113eb"
      className="py-32 flex w-full justify-center align-middle"
    >
      <div
        data-testid="div-d4c2ae09"
        className="flex flex-col justify-center align-middle"
      >
        <div data-testid="div-e4259329" className="flex justify-center py-2">
          <i
            data-testid="i-148a5ea3"
            className="fa fa-spinner fa-spin text-5xl text-green"
          />
        </div>
        <h4 data-testid="h4-925412f4">
          Access not allowed. Redirecting in {count} seconds...
        </h4>
      </div>
    </div>
  );
};

class MasterList extends Component {
  constructor(props) {
    super(props);
    this.gridRef = createRef();
    this.state = {
      breadcrumbs: { pages: [] },
      report: null,
      group: null,
      isSavingNewReport: false,
      showConfirmDeleteReportModal: false,
      showSaveModal: false,
      showSaveNewModal: false,
      sidebarState: 'expanded',
      columnsChanged: false,
      filterChanged: false,
      applyingSavedFilters: false,
      showDateInfoAlert: true,
      filterModel: {},
      stopGroupStats: {},
      userAllowed: SessionStore.canAccessUnifiedView(), // For now this is limited to admin users
      changeCarrierDisabled: true,
      changeShipperDisabled: true,
      arrivedActionDisabled: true,
      columnDefs: this.getColumnDefs('drayage', props.t),
      view: 'drayage',
      columns: [],
      statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent' }] },
      frameworkComponents: {
        deliveryOrderFormatter,
        statusFormatter: statusFormatter,
        portIntegrationsFormatter: portIntegrationsFormatter,
        checkedFormatter: checkedFormatter,
        lineSeparatedStringFormatter: lineSeparatedStringFormatter,
        tagFormatter: tagFormatter,
        orderIDRenderer: orderIDRenderer,
        shipmentIDRenderer: shipmentIDRenderer,
        marginFormatter: marginFormatter,
        textFilterWithExclude: TextFilterWithExclude,
        textFilter: TextFilter,
      },
      showSaveFilterModal: false,
      localFilters: [],
      sharedFilters: [],
      filterSelected: false,
      canShare: SessionStore.canShareFilters(),
      shared: false,
      groupDefaultExpanded: 2,
      selectedFilter: {
        name: '',
        id: '',
      },
      order_oid: '',
      showOrderModal: false,
      shipment_oid: '',
      showShipmentModal: false,
      isFirst: true,
      getRowNodeId: function (data) {
        return data._id;
      },
      selectedShipments: [],
      selectedShipmentsForAccessorials: [],
      selectedShipmentsForYards: [],
      loadingData: true,
      activePage: 1,
      pages: 1,
      queryPayload: {
        params: {},
        options: {
          per_page: 100,
          sort: [DEFAULT_CREATED_AT_SORT],
        },
      },
      shouldShowDefaultFilterInfo: true,
    };
  }

  getColumnDefs(view, t) {
    const columns =
      view === 'drayage' ? getDrayageColumns() : getShorthaulColumns();
    return columns
      .map((incoming) => {
        const column = { ...incoming };
        column.menuTabs = ['filterMenuTab', 'generalMenuTab'];

        if (column.field === 'created_at') {
          column.filterParams.minValidDate = getMinValidDate();
        }

        if (column.field === 'order_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onOrderClick: (data) => {
              const { order_oid } = data;
              this.setState({
                order_oid,
                showOrderModal: true,
              });
            },
          };
        } else if (column.field === 'shipment_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onShipmentClick: (data) => {
              const { shipment_oid } = data;
              this.setState({
                shipment_oid,
                showShipmentModal: true,
              });
            },
          };
        }

        if (
          isUS() &&
          column.field &&
          column.field.toLowerCase().includes('state')
        ) {
          column.filter = 'agSetColumnFilter';
          column.floatingFilter = true;
          column.filterParams = {
            values: USStates.map((state) => state.abbreviation),
          };
        }
        return column;
      })
      .map((c) => {
        if (c.field === 'margin') {
          c.cellRenderer = 'marginFormatter';
        }
        c.headerName = c.titleize
          ? firstLetterTitlization(t(sluggify(c.headerName)))
          : t(sluggify(c.headerName));
        c.headerTooltip = t(sluggify(c.headerTooltip));
        return c;
      });
  }

  startPolling() {
    this.pollingTimer = setInterval(
      () => this.getFilterResults(true),
      POLLING_FREQUENCY,
    );
  }

  stopPolling() {
    clearInterval(this.pollingTimer);
  }

  async componentDidMount() {
    FiltersStore.addChangeListener(this.handleFiltersStoreUpdate);
    FiltersStore.addFiltersChangeListener(this.handleFiltersUpdate);
    ColumnsSetStore.addChangeListener(this.handleColumnUpdate);

    FilterActionCreators.getFilterNames();
    ColumnsWebAPIUtils.getCols();

    document.title = 'Cargomatic Inc., - Command: Unified View';

    // fetch report
    if (this.props.match?.params?.reportId) {
      const {
        match: { params },
      } = this.props;

      const report = await getReport({
        queryKey: ['getReport', params.reportId],
      });

      this.setState({ report }, () => {
        this.handleFilterSelect(report);
        this.loadBreadCrumbs();
      });
    } else {
      this.loadBreadCrumbs();
    }

    this.startPolling();
  }

  abortControllers() {
    // Count and queries abort controllers.
    if (this.abortCountController) {
      if (!this.abortCountController.signal.aborted) {
        this.abortCountController.abort();
      }
    }
    if (this.abortQueryController) {
      if (!this.abortQueryController.signal.aborted) {
        this.abortQueryController.abort();
      }
    }
  }

  componentWillUnmount() {
    FiltersStore.removeChangeListener(this.handleFiltersStoreUpdate);
    FiltersStore.removeFiltersChangeListener(this.handleFiltersUpdate);
    ColumnsSetStore.removeChangeListener(this.handleColumnUpdate);
    this.stopPolling();
    document.title = 'Cargomatic Inc. - Command';
  }

  handleUpdateRows = (updates) => {
    const map = updates.reduce((map, update) => {
      map[update._id] = update;
      return map;
    }, {});
    this.setState((prevState) => {
      const items = prevState.items.map((item) => {
        if (map[item.shipment_oid]) {
          return {
            ...item,
            ...map[item.shipment_oid],
          };
        }
        return item;
      });
      return { items };
    });
  };

  handleToggleFavorite = async () => {
    if (!this.state.report) {
      return;
    }

    const isFavorite = !this.state.report.isFavorite;

    const { isSuccess } = await setFavoriteReport({
      id: this.state.report._id,
      isFavorite,
    });

    if (!isSuccess) {
      return;
    }

    this.setState((prevState) => ({
      report: {
        ...prevState.report,
        isFavorite,
      },
    }));
  };

  handleColumnUpdate = (update) => {
    const { columns, event } = update;
    if (event === 'CREATE') {
      this.setState(
        {
          columns,
        },
        () => {
          // this.reset();
        },
      );
    } else {
      this.setState({
        columns,
      });
    }
  };

  getSelectedRows() {
    if (!this.api) {
      return [];
    }
    return this.api.getSelectedRows().map((s) => ({
      ...s,
      _id: s.shipment_oid,
      id: s.shipment_oid,
    }));
  }

  onHideShipmentModal = () => {
    this.setState({
      showShipmentModal: false,
    });
  };

  onHideModal = () => {
    this.setState({
      showOrderModal: false,
    });
  };

  onGridReady = (params) => {
    this.api = params.api;
    this.columnApi = params.columnApi;
    this.api.showLoadingOverlay();
    this.api.autoSizeColumns();

    this.api.addEventListener('modelUpdated', () => {
      this.handleRowDisplayed();
    });

    this.api.addEventListener('columnVisible', this.onColumnVisible);
    this.api.addEventListener('filterChanged', this.onFilterChange);
    this.api.addEventListener('cellEditingStopped', this.onEditingStopped);

    this.getFilterResults();
  };

  onEditingStopped = (event) => {
    if (!event.valueChanged || !event.colDef.updateFunc) {
      return;
    }

    event.colDef.updateFunc(event);
  };

  onColumnVisible = (event) => {
    const { visible, column } = event;

    const colId = column?.colId;

    if (!colId) {
      return;
    }

    const res = getStopPrefixAndField(colId);
    if (!res.isStopGroupField) {
      return;
    }
    const count = this.state.stopGroupStats[res.prefix];

    if (count === 1) {
      return;
    }

    const { prefix, field } = res;

    const otherColumns = Array.from(Array(count).keys())
      .filter((index) => {
        const stopGroupField = `${prefix}__${index}__${field}`;
        if (colId === stopGroupField) {
          return false;
        }

        const col = this.columnApi.getColumn(stopGroupField);
        if (col.visible === visible) {
          return false;
        }
        return true;
      })
      .map((index) => {
        const colId = `${prefix}__${index}__${field}`;

        return {
          colId,
          hide: !visible,
        };
      });

    if (otherColumns.length === 0) {
      return;
    }

    this.api.applyColumnState({ state: otherColumns, applyOrder: true });
  };

  onFilterChange = () => {
    if (this.state.isQuickFiltered) return;

    this.setState(
      {
        filterChanged: true,
      },
      () => {
        this.handlePagination(1);
      },
    );
  };

  onSortChanged = () => {
    if (this.state.isQuickFiltered) return;

    this.getFilterResults();
  };

  handleFiltersUpdate = (update) => {
    const { filters, event } = update;
    const localFilters = filters
      .filter((filter) => filter.shared !== true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b.name.trim()));
    const sharedFilters = filters
      .filter((filter) => filter.shared === true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b?.name?.trim()));
    if (event === 'CREATED') {
      const {
        result: { shared, _id },
      } = update;
      const cols = [];
      this.api.getAllGridColumns().forEach((col) => {
        const { userProvidedColDef, visible } = col;
        if (!isNull(userProvidedColDef)) {
          const { field } = userProvidedColDef;
          if (visible) {
            cols.push(field);
          }
        }
      });
      const filter_id = _id;
      if (!shared) {
        ColumnsWebAPIUtils.createCols({ filter_id, cols });
      }
    }

    this.setState({
      localFilters,
      sharedFilters,
    });
  };

  handleRowDisplayed = () => {
    this.setState({ rowCount: this.api.getDisplayedRowCount() });
  };

  handleFiltersStoreUpdate = (row) => {
    const { add, update } = row;

    if (this.state.isFirst) {
      this.api.batchUpdateRowData({ add });
      this.setState({
        isFirst: false,
      });
    } else {
      if (add.length > 0) {
        this.api.batchUpdateRowData({ add });
        this.handleRowDisplayed();
      }
      if (update.length > 0) {
        this.api.batchUpdateRowData({ update });
      }
    }
  };

  getSavedFilterModel = (filter) => {
    // move primaryDate to model to have backward compatibility
    if (filter.primaryDate && filter.timeSelected?.length) {
      // check if model has same column filter we do nothing
      if (!(filter.model && filter.model[filter.primaryDate])) {
        // model can not exist
        filter.model = filter.model || {};
        // set primary
        const timeSelectedType = TIME_SELECTIONS.find(
          (ts) => ts.name === filter.timeSelected,
        )?.localizationSlug;
        if (timeSelectedType) {
          filter.model[filter.primaryDate] = {
            dateFrom: null,
            dateTo: null,
            filterType: 'date',
            type: timeSelectedType,
          };
        }
      }
    }

    if (!filter.model) {
      return {};
    }
    const mappedFilterModel = Object.keys(filter.model).reduce((model, key) => {
      const value = filter.model[key];
      model[key] = value;

      // remove empty filters
      if (Array.isArray(value.value) && value.value.length === 0) {
        delete model[key];
      }

      // remove not used condition to not treat them as new default condition, see: CARGO-7936
      const notUsedConditions = ['contains', 'notContains'];
      // plain filter
      if (notUsedConditions.includes(value.type)) {
        delete model[key];
      }
      // filter with multiple conditions
      const conditions = Object.keys(value).filter((key) =>
        key.startsWith('condition'),
      );
      if (conditions.length) {
        conditions.forEach((conditionKey) => {
          if (notUsedConditions.includes(value[conditionKey].type)) {
            delete model[key];
          }
          // if array of conditions
          if (
            Array.isArray(value[conditionKey]) &&
            value[conditionKey].length
          ) {
            value[conditionKey].forEach((conditionFilter) => {
              if (notUsedConditions.includes(conditionFilter.type)) {
                delete model[key];
              }
            });
          }
        });
      }

      return model;
    }, {});

    return mappedFilterModel;
  };

  setCustomFilterModels = (savedFilterModel) => {
    const customFilterColumns = [
      'shipment_id',
      'order_id',
      'orderStatus',
      'chassis_number',
      'shipper_name',
      'reference_numbers',
      ...STANDARD_REFERENCE_NUMBERS,
    ];

    const stopColumnFields = this.state.columnDefs
      .filter((c) => c.children)
      .flatMap((c) => c.children)
      .map((c) => c.field);
    const customFilterStopColumns = stopColumnFields.filter(
      (f) =>
        f &&
        (f.endsWith('stop_equipment_id') || f.endsWith('stop_chassis_number')),
    );

    const customFilterFields = [
      ...customFilterColumns,
      ...customFilterStopColumns,
    ];

    customFilterFields.forEach((field) => {
      const filterInstance = this.api.getFilterInstance(field);
      if (!isEmpty(filterInstance)) {
        filterInstance.setModel(
          savedFilterModel && savedFilterModel[field]
            ? savedFilterModel[field]
            : null,
        );
      }
    });
  };

  handleFilterSelect = async (selectedFilter) => {
    if (selectedFilter._id !== this.state.selectedFilter._id) {
      let filter = {};
      // get full filter model from the BE
      const result = await getFilter(selectedFilter._id);
      if (result) {
        filter = result.entity;

        let mappedColumns;
        this.setState(
          (prevState) => {
            const { columns } = prevState;
            const columnSet = find(columns, (col) => {
              return col.filter_id === filter._id;
            });

            mappedColumns = this.getColumnDefs(filter.view, this.props.t);
            const mappedFilterModel = this.getSavedFilterModel(filter);

            return {
              view: filter.view,
              selectedFilter: filter,
              filterSelected: true,
              activeCols: columnSet ? columnSet._id : null,
              applyingSavedFilters: true,
              columnDefs: mappedColumns,
              filterModel: { ...mappedFilterModel },
            };
          },
          async () => {
            this.api.autoSizeColumns();
            await this.api.setGridOption('columnDefs', mappedColumns);
            loadColumnState(filter, this.api);
            this.setCustomFilterModels(this.state.filterModel);
            this.api.setFilterModel(this.state.filterModel);
            this.api.onFilterChanged();
            if (filter.rowGroupColumns && filter.rowGroupColumns?.length > 0) {
              this.api.setRowGroupColumns(filter.rowGroupColumns);
            }
          },
        );
      }
    } else {
      this.setState(
        {
          filterSelected: false,
          selectedFilter: { name: '', _id: '' },
          applyingSavedFilters: true,
          filterModel: null,
        },
        () => {
          this.setCustomFilterModels(null);
          this.api.setFilterModel(null);
          this.api.onFilterChanged();
        },
      );
    }
  };

  getModelParams() {
    const { view } = this.state;
    const filterModel = cloneDeep(this.api.getFilterModel());

    return getModelParams({
      view,
      filterModel,
      agGridApi: this.api,
    });
  }

  exportCsv = async () => {
    const params = this.transformFilterModelQuery(this.getModelParams());

    const options = {
      sort: this.getSortModel(),
    };
    const sameDay = moment(this.state.dateTo).isSame(
      moment(this.state.dateFrom, 'day'),
    );

    const columns = this.api
      .getAllGridColumns()
      .filter((c) => c.visible && c.colDef.field)
      .map((c) => {
        // BE expecting field for data mapping and actual headerName for csv header
        return {
          field: c.colDef.field,
          headerName: c.colDef.headerName,
        };
      });

    this.api.showLoadingOverlay();
    this.setState({ exportLoading: true }, async () => {
      if (this.state.primaryDate) {
        params[this.state.primaryDate] = {
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          type: sameDay ? 'equals' : 'inRange',
          filterType: 'date',
        };
      }

      await getUVCsvExport({
        params,
        options,
        columns,
      });
      this.api.hideOverlay();
      this.setState({ exportLoading: false });
    });
  };

  hideSaveFilterModal = () => {
    this.setState({ showSaveFilterModal: false });
  };

  reset = () => {
    this.api.setFilterModel(null);
    this.setState(
      (prevState) => {
        const { view } = prevState;
        this.api.setQuickFilter(null);
        const columnDefs = this.getColumnDefs(view, this.props.t);
        const model =
          view === 'drayage' ? drayageFilterModel : shorthaulFilterModel;
        const queryPayload = {
          options: {
            page: 1,
            per_page: 100,
            sort: [DEFAULT_CREATED_AT_SORT],
          },
          params: {
            ...model,
            ...createdAtDefaultFilterModel,
          },
        };
        return {
          filterSelected: false,
          selectedFilter: { id: '', name: '' },
          activeCols: null,
          columnDefs,
          queryPayload,
          activePage: 1,
          filterModel: {},
        };
      },
      () => {
        this.api.setColumnDefs(this.state.columnDefs);
        this.api.autoSizeColumns();
        this.api.resetColumnState();
        this.setCustomFilterModels(null);
        // reset the columns filter state
        this.api.setFilterModel(null);
        this.api.onFilterChanged();
      },
    );
  };

  getColumnState() {
    const columnState = this.api.getColumnState();
    const pinned = columnState
      .filter((c) => c.pinned)
      .map((c) => ({ field: c.colId, pinned: c.pinned }));
    const hidden = columnState.filter((c) => c.hide).map((c) => c.colId);
    const columnOrder = columnState.map((c) => c.colId);

    return { columnOrder, pinned, hidden };
  }

  handleViewChange = (view) => {
    this.setState(
      (prevState) => {
        // get the columns by view and the stop group column groups
        const columnDefs = this.getColumnDefs(view, this.props.t);
        const update = {
          activePage: 1,
          view,
          columnDefs,
        };

        if (prevState.view !== view) {
          update.filterSelected = false;
          update.selectedFilter = { id: '', name: '' };
        }

        return update;
      },
      () => {
        this.api.setColumnDefs(this.state.columnDefs);
        this.api.autoSizeColumns();
        this.api.resetColumnState();
        // reset the columns filter state
        this.api.setFilterModel(null);
      },
    );
  };

  getRowGroupColumns = () => {
    return this.api.getRowGroupColumns().map((col) => col.colId);
  };

  getColumnsSize() {
    const columnsState = this.api.getColumnState();

    return columnsState.map((columnState) => {
      return { field: columnState.colId, size: columnState.width };
    });
  }

  getColumnSorting() {
    const columnsState = this.api.getColumnState();

    return columnsState.map((columnState) => {
      return {
        field: columnState.colId,
        sort: columnState.sort,
        sortIndex: columnState.sortIndex,
      };
    });
  }

  saveAs = (name) => {
    const { shared, view } = this.state;
    const model = cloneDeep(this.api.getFilterModel());
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const canEdit = false;
    const { columnOrder, pinned, hidden } = this.getColumnState();
    const newFilter = {
      name,
      model,
      rowGroupColumns,
      shared,
      view,
      canEdit,
      pinned,
      hidden,
      columnOrder,
      rowColumnsSize,
      rowColumnsSorting,
    };

    FilterActionCreators.createFilter(newFilter);
    this.hideSaveFilterModal();
  };

  update = (name) => {
    const { selectedFilter, shared, view } = this.state;
    let _id;
    const model = cloneDeep(this.api.getFilterModel());
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const { columnOrder, pinned, hidden } = this.getColumnState();
    const updates = {
      id: selectedFilter._id,
      model,
      rowGroupColumns,
      name,
      shared,
      view,
      pinned,
      hidden,
      columnOrder,
      rowColumnsSize,
      rowColumnsSorting,
    };

    FilterActionCreators.updateFilter(updates);

    if (this.state.activeCols) {
      _id = this.state.activeCols;
      const cols = [];
      this.api.getAllGridColumns().forEach((col) => {
        const { userProvidedColDef, visible } = col;
        if (!isNull(userProvidedColDef)) {
          const { field } = userProvidedColDef;
          if (visible) {
            cols.push(field);
          }
        }
      });
      if (!shared) {
        ColumnsWebAPIUtils.updateCols({ _id, cols });
      }
    }

    this.hideSaveFilterModal();
  };

  toggleShare = () => {
    const { shared } = this.state;
    this.setState({
      shared: !shared,
    });
  };

  handleRowSelection = () => {
    const selectedRows = this.api.getSelectedRows() || [];
    let arrivedActionDisabled = true;

    let changeCustomerDisabled = false;
    if (selectedRows.length) {
      arrivedActionDisabled = !selectedRows.every(
        (s) => !!s.order_oid && ARRIVED_ORDER_TYPES.includes(s.order_type),
      );
      changeCustomerDisabled = selectedRows.some((s) =>
        INACTIVE_SHIPMENT_STATUS.includes(s.shipmentStatus),
      );
      const changeCarrierDisabled =
        !this.areShipmentsWithSameShipper(selectedRows);
      const selectedShipments = uniq(
        selectedRows.filter((s) => !!s.shipment_oid).map((s) => s.shipment_oid),
      );

      const selectedShipmentsForAccessorials = uniq(
        selectedRows
          .filter(
            (s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s),
          )
          .map((shipment) => {
            const {
              shipment_oid: _id,
              shipment_id,
              order_oid: orderId,
            } = shipment;

            return {
              ...shipment,
              _id,
              shipment_id,
              stops: getStopGroupsArray(shipment),
              orderId,
            };
          }),
      );

      const selectedShipmentsForYards = uniq(
        selectedRows
          .filter(
            (s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s),
          )
          .map((shipment) => {
            // get equipment ids from stop groups
            const stopGroups = getStopGroupsArray(shipment);

            const mappedStopEquipments = stopGroups.flatMap((s) => {
              const equipments =
                s.stop_equipment_oid !== '' ? [s.stop_equipment_id] : [];
              return equipments;
            });

            // distinct
            const equipments =
              mappedStopEquipments?.length > 0
                ? [...new Set(mappedStopEquipments)]
                : [];

            return {
              id: shipment.shipment_oid,
              equipments: equipments,
              shipment_id: shipment.shipment_id,
              market_id: shipment.market_code,
            };
          }),
      );

      this.setState({
        arrivedActionDisabled,
        changeCarrierDisabled: changeCustomerDisabled || changeCarrierDisabled,
        changeShipperDisabled: changeCustomerDisabled,
        selectedShipments,
        selectedShipmentsForAccessorials,
        selectedShipmentsForYards,
      });
    } else {
      this.setState({
        arrivedActionDisabled,
        changeCarrierDisabled: changeCustomerDisabled,
        changeShipperDisabled: changeCustomerDisabled,
        selectedShipments: [],
        selectedShipmentsForAccessorials: [],
        selectedShipmentsForYards: [],
      });
    }
  };

  getDataTotal = () => {
    const { userAllowed } = this.state;
    if (!userAllowed) {
      return;
    }

    this.setState(async (prevState) => {
      this.abortCountController = new AbortController();

      const totalCount = await getShipmentsCount(
        {
          params: {
            ...prevState.queryPayload.params,
          },
        },
        { controller: this.abortCountController },
      );
      const pages = Math.ceil(
        totalCount / prevState.queryPayload.options.per_page,
      );

      this.setState({ totalCount, pages });
    });
  };

  getSortModel = () => {
    return this.api.getColumnState().filter((col) => !!col.sort);
  };

  getSortModelInsteadOfDefaultSort = () => {
    return this.api
      .getColumnState()
      .filter(
        (col) =>
          !!col.sort && col.sortIndex < DEFAULT_CREATED_AT_SORT.sortIndex,
      );
  };

  transformFilterModelQuery() {
    const modelParams = this.getModelParams();

    return transformFilterModelQuery(modelParams);
  }

  isCreatedAtSortApplied = (columnsState) => {
    return columnsState.find(
      (item) =>
        item.colId === DEFAULT_CREATED_AT_SORT.colId &&
        item.sortIndex !== DEFAULT_CREATED_AT_SORT.sortIndex &&
        item.sortIndex !== null,
    );
  };

  getFilterResults = async (isPolling = false) => {
    const {
      userAllowed,
      activePage: page,
      queryPayload: {
        options: { per_page },
      },
    } = this.state;

    if (!userAllowed) {
      return;
    }

    if (this.api) {
      if (!isPolling) {
        this.api.showLoadingOverlay();
      }

      // make the query string for the shared/pesonal filters
      let params = this.transformFilterModelQuery();
      const options = {
        sort: this.getSortModelInsteadOfDefaultSort(),
        per_page,
        page,
      };

      const columnsState = this.api.getColumnState();

      if (!this.isCreatedAtSortApplied(columnsState)) {
        const createdAtSort = columnsState.find(
          (col) => col.colId === DEFAULT_CREATED_AT_SORT.colId,
        );
        createdAtSort.sort = DEFAULT_CREATED_AT_SORT.sort;
        createdAtSort.sortIndex = DEFAULT_CREATED_AT_SORT.sortIndex;
        options.sort.push({ ...createdAtSort });
      }

      this.setState(
        (previousState) => {
          return {
            ...previousState,
            loadingData: true,
            activePage: page || 1,
            queryPayload: {
              ...previousState.queryPayload,
              params,
              options,
            },
          };
        },
        async () => {
          // Any pending requests should abort.
          this.abortControllers();

          this.abortQueryController = new AbortController();

          // Get Count
          this.getDataTotal();

          // Get Query Results
          const data = await getShipmentsByStream(
            {
              params,
              options,
            },
            {
              controller: this.abortQueryController,
            },
          );
          this.handleDataStream(data);
        },
      );
    }
  };

  handleDataStream = async (data) => {
    try {
      const { items } = data;

      this.setState(() => {
        const updates = {
          items,
          loadingData: false,
          applyingSavedFilters: false,
        };

        return updates;
      });
    } catch (e) {
      console.error(e);
    }
  };

  handlePerPageChange = (per_page) => {
    this.setState(
      (prevState) => ({
        activePage: 1,
        queryPayload: {
          ...prevState.queryPayload,
          options: {
            ...prevState.options,
            per_page: parseInt(per_page, 10),
          },
        },
      }),
      () => this.getFilterResults(),
    );
  };

  handlePagination = (page) => {
    this.setState({ activePage: page }, () => {
      this.getFilterResults();
    });
  };

  renderPagination() {
    return (
      <span
        data-testid="span-5106d5a3"
        className="flex justify-between lg:space-x-8 items-center"
      >
        <Pagination
          dataTestId="Pagination-48096613"
          first
          last
          total={this.state.totalCount}
          page={this.state.activePage}
          perPage={this.state.queryPayload.options.per_page}
          onPageClick={(page) => this.handlePagination(page)}
        />
        <PanelSection
          dataTestId="PanelSection-63cdade0"
          className="flex space-x-2 items-center"
        >
          <PerPage
            options={perPageOptions}
            onChange={(e) => this.handlePerPageChange(e.target.value)}
            selectedValue={this.state.queryPayload.options.per_page}
          />
          <ColumnCategoriesDropdown api={this.gridRef.current?.api} />
        </PanelSection>
      </span>
    );
  }

  areShipmentsWithSameShipper = (selectedRows) => {
    if (selectedRows.length === 1) {
      return true;
    }
    return (
      selectedRows[0]?.shipper_oid &&
      selectedRows.every((shipment) => {
        return shipment.shipper_oid === selectedRows[0].shipper_oid;
      })
    );
  };

  handleShowSaveReportModal = () => {
    this.setState({ showSaveModal: true, isSavingNewReport: false });
  };

  handleHideSaveReportModal = () => {
    this.setState({ showSaveModal: false });
  };

  handleShowSaveNewReportModal = () => {
    this.setState({ showSaveModal: true, isSavingNewReport: true });
  };

  handleShowConfirmDeleteReportModal = () => {
    this.setState({ showConfirmDeleteReportModal: true });
  };

  handleHideConfirmDeleteReportModal = () => {
    this.setState({ showConfirmDeleteReportModal: false });
  };

  handleConfirmDelete = async () => {
    const { report } = this.state;
    if (!report) {
      return;
    }
    await deleteReport({ ...report, id: report._id });
    this.handleHideConfirmDeleteReportModal();
    this.props.history.push('/unifiedview/explore', { shallow: true });
    this.setState({ report: null }, () => {
      this.loadBreadCrumbs();
    });
  };

  handleSaveReport = async (report) => {
    const id = report?.id;
    this.handleHideSaveReportModal();

    const { view } = this.state;
    const model = cloneDeep(this.api.getFilterModel());
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const canEdit = false;
    const { columnOrder, pinned, hidden } = this.getColumnState();
    let reportToSave = report;

    if (id) {
      const updatedReport = {
        model,
        rowGroupColumns,
        view,
        pinned,
        hidden,
        columnOrder,
        rowColumnsSize,
        rowColumnsSorting,
        ...report,
      };
      reportToSave = await updateReport(updatedReport);
    } else {
      const newReport = {
        model,
        rowGroupColumns,
        view,
        canEdit,
        pinned,
        hidden,
        columnOrder,
        rowColumnsSize,
        rowColumnsSorting,
        ...report,
      };
      reportToSave = await createReport(newReport);

      this.props.history.push(`/unifiedview/reports/${reportToSave._id}`, {
        shallow: true,
      });
    }

    this.setState({ report: reportToSave }, () => {
      this.loadBreadCrumbs();
    });
  };

  removeFilter = (filterName) => {
    const filterModel = cloneDeep(this.api.getFilterModel());

    delete filterModel[filterName];

    this.setState({ filterModel }, () => {
      this.api.setFilterModel(filterModel);
      this.api.onFilterChanged();
    });
  };

  loadBreadCrumbs() {
    const { t, pageContext, groups } = this.props;

    const { updatePage } = pageContext || {};
    if (this.state.report) {
      const { report } = this.state;
      const pages = [];
      let group;

      if (report.groupId) group = groups.getGroupById(report.groupId);

      if (pageContext.previousPage) {
        pages.push({
          name: pageContext.previousPageName,
          href: pageContext.previousPage,
        });

        if (pageContext.previousPageName === group?.name) {
          let currentGroup = group;
          while (currentGroup?.parent) {
            const parentGroup = groups.getGroupById(currentGroup.parent);
            pages.unshift({
              name: parentGroup.name,
              href: `/unifiedview/groups/${parentGroup._id}/reports`,
            });
            currentGroup = parentGroup;
          }
        }
      } else if (group) {
        pages.push({
          name: group.name,
          href: `/unifiedview/groups/${report.groupId}/reports`,
        });

        let currentGroup = group;
        while (currentGroup?.parent) {
          const parentGroup = groups.getGroupById(currentGroup.parent);
          pages.unshift({
            name: parentGroup.name,
            href: `/unifiedview/groups/${parentGroup._id}/reports`,
          });
          currentGroup = parentGroup;
        }
      }

      const breadcrumbs = {
        homeUrl: '/unifiedview/reports',
        onClick: updatePage,
        pages: [
          ...pages,
          {
            name: report.name,
            href: `/unifiedview`,
            current: true,
            render: () => {
              return (
                <div
                  data-testid="div-ae8c4f22"
                  className="flex items-center space-x-2"
                >
                  <span data-testid="span-ede2cd20">{report.name}</span>
                  <DropdownMenu dataTestId="DropdownMenu-c7f46423" useIcon>
                    {this.state.report && (
                      <Menu.Item
                        data-testid="MenuItem-3e12d060"
                        key="save_report"
                      >
                        <DropdownItem
                          useIcon
                          dataTestId="updateReportButton"
                          onClick={this.handleShowSaveReportModal}
                          disabled={
                            !SessionStore.isAbleToManageUVReport(report)
                          }
                          disabledText={t(
                            'YOU_HAVE_NO_PERMISSIONS_TO_UPDATE_REPORT',
                          )}
                        >
                          {t('UPDATE_REPORT')}
                        </DropdownItem>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      data-testid="MenuItem-9e4e87bb"
                      key="save_new_report"
                    >
                      <DropdownItem
                        useIcon
                        dataTestId="saveNewReportButton"
                        onClick={this.handleShowSaveNewReportModal}
                      >
                        {t('SAVE_NEW_REPORT')}
                      </DropdownItem>
                    </Menu.Item>
                    {this.state.report && (
                      <Menu.Item
                        data-testid="MenuItem-dad3bdaa"
                        key="delete_rate"
                      >
                        <DropdownItem
                          dataTestId="deleteReportButton"
                          onClick={this.handleShowConfirmDeleteReportModal}
                          disabled={
                            !SessionStore.isAbleToManageUVReport(report)
                          }
                          disabledText={t(
                            'YOU_HAVE_NO_PERMISSIONS_TO_DELETE_REPORT',
                          )}
                        >
                          {t('DELETE_REPORT')}
                        </DropdownItem>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      data-testid="MenuItem-70fc7fdc"
                      key="export_report"
                    >
                      <DropdownItem
                        useIcon
                        dataTestId="exportReportButton"
                        onClick={this.exportCsv}
                      >
                        {t('EXPORT_TO_CSV')}
                      </DropdownItem>
                    </Menu.Item>
                  </DropdownMenu>
                </div>
              );
            },
          },
        ],
      };
      this.setState({ breadcrumbs });

      return;
    }
    const breadcrumbs = {
      homeUrl: '/unifiedview/reports',
      onClick: updatePage,
      pages: [
        {
          name: 'Explore',
          href: `/unifiedview/explore`,
          current: true,
          render: () => {
            return (
              <div
                data-testid="div-50c7a082"
                className="flex items-center space-x-2"
              >
                <span data-testid="span-7e48723e">{t('EXPLORE')}</span>
                <DropdownMenu dataTestId="DropdownMenu-b4609936" useIcon>
                  <Menu.Item
                    data-testid="MenuItem-34e9993e"
                    key="save_new_report"
                  >
                    <DropdownItem
                      useIcon
                      dataTestId="saveNewReportButton"
                      onClick={this.handleShowSaveNewReportModal}
                    >
                      {t('SAVE_NEW_REPORT')}
                    </DropdownItem>
                  </Menu.Item>
                  <Menu.Item
                    data-testid="MenuItem-639b165e"
                    key="export_report"
                  >
                    <DropdownItem
                      useIcon
                      dataTestId="exportReportButton"
                      onClick={this.exportCsv}
                    >
                      {t('EXPORT_TO_CSV')}
                    </DropdownItem>
                  </Menu.Item>
                </DropdownMenu>
              </div>
            );
          },
        },
      ],
    };
    this.setState({ breadcrumbs });
  }

  render() {
    const { userAllowed } = this.state;
    if (!userAllowed) {
      return <CountDown />;
    }

    const { t } = this.props;

    const { match } = this.props;
    const params = match?.params;
    const groupId = this.state.report?.groupId;

    const activeTab = groupId ? 'reports' : 'explore';

    const isSavedReport = !!params?.reportId;

    return (
      <Layout
        groupId={groupId}
        activeTab={activeTab}
        sidebarDefaultState={SIDEBAR_STATUSES.COLLAPSED}
      >
        <div data-testid="div-d75254a9" className="px-4 sm:px-6 lg:px-8">
          <div data-testid="div-84559398" className="">
            <div data-testid="div-27313d28" className="flex space-x-4">
              <Breadcrumbs
                dataTestId="Breadcrumbs-ea92556d"
                breadcrumbs={this.state.breadcrumbs}
              />
              {this.state.report && (
                <Favorite
                  selected={this.state.report?.isFavorite}
                  onToggle={this.handleToggleFavorite}
                />
              )}
            </div>
            <Panel dataTestId="Panel-1084a244">
              <PanelSection dataTestId="PanelSection-fe125a89">
                <div
                  data-testid="div-43cab497"
                  className="flex justify-between flex-wrap items-center space-x-4"
                >
                  <FilterChips
                    className="flex-1"
                    columnDefs={this.state.columnDefs}
                    filterModel={this.api?.getFilterModel()}
                    onRemove={this.removeFilter}
                    view={this.state.view}
                  />
                  <div
                    data-testid="div-d4fc9b45"
                    className="flex space-x-4 items-center"
                  >
                    <a
                      data-testid="a-5708e095"
                      href="#"
                      className="w-20 block"
                      onClick={this.reset}
                    >
                      {t('CLEAR_FILTERS')}
                    </a>
                    {!isSavedReport && (
                      <div data-testid="selectShipmentType">
                        <Select
                          value={this.state.view}
                          onValueChange={this.handleViewChange}
                        >
                          <SelectTrigger className="">
                            <SelectValue placeholder={t('SELECT_VIEW')} />
                          </SelectTrigger>
                          <SelectContent>
                            {getOptions(t).map((option) => (
                              <SelectItem
                                value={option.value}
                                key={option.value}
                                data-testid={`selectShipmentType-SelectItem-${option.value}`}
                              >
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <BulkActions
                      view={this.state.view}
                      disabled={this.state.selectedShipments.length === 0}
                      changeCarrierDisabled={this.state.changeCarrierDisabled}
                      changeShipperDisabled={this.state.changeShipperDisabled}
                      arrivedActionDisabled={this.state.arrivedActionDisabled}
                      selectedShipments={this.state.selectedShipments}
                      selectedRows={this.getSelectedRows()}
                      selectedShipmentsForYards={
                        this.state.selectedShipmentsForYards
                      }
                      selectedShipmentsForAccessorials={
                        this.state.selectedShipmentsForAccessorials
                      }
                      onBillingStatusPendingReviewUpdate={this.handleUpdateRows}
                    />
                  </div>
                </div>
              </PanelSection>
              {this.renderPagination()}
              <div
                data-testid="div-da5f52c8"
                className="mt-2 ag-theme-balham"
                style={{ height: 700 }}
              >
                <AgGridReact
                  ref={this.gridRef}
                  rowData={this.state.items}
                  immutableData
                  maintainColumnOrder
                  rememberGroupStateWhenNewData
                  animateRows
                  getRowId={(params) => params.data._id}
                  suppressClickEdit
                  suppressRowClickSelection
                  enableCellTextSelection
                  enableRangeSelection
                  rowSelection="multiple"
                  getRowNodeId={this.state.getRowNodeId}
                  frameworkComponents={this.state.frameworkComponents}
                  multiSortKey="ctrl"
                  onGridReady={this.onGridReady}
                  onSortChanged={this.onSortChanged}
                  statusBar={this.state.statusBar}
                  columnDefs={this.state.columnDefs}
                  defaultColDef={gridOpts.defaultColDef}
                  onSelectionChanged={this.handleRowSelection}
                  localeText={getLocaleText()}
                  overlayLoadingTemplate={`<div><span class="text-base text-green font-bold">${t(
                    'LOADING',
                  )}</span><img src="https://3qjglz4aka5y3jyx13q7je0p-wpengine.netdna-ssl.com/wp-content/uploads/2021/01/logo-header.svg" alt="" class="img-fluid"></div>`}
                  sideBar={sidebarParams}
                  allowDragFromColumnsToolPanel
                />
              </div>
            </Panel>
          </div>

          {this.state.showSaveFilterModal && (
            <SaveFilterModal
              filterSelected={this.state.filterSelected}
              selectedFilter={this.state.selectedFilter}
              onUpdate={this.update}
              setShareWithTeam={this.toggleShare}
              share={this.state.shared}
              onSave={this.saveAs}
              show
              onHide={this.hideSaveFilterModal}
              canShareWithTeam={this.state.canShare}
            />
          )}
          {this.state.showOrderModal && (
            <CargomaticModal
              dataTestId="CargomaticModal-226ea836"
              show
              location="top"
              title={titleize(t('DETAIL', { count: 2 }))}
              onHide={this.onHideModal}
              body={<OrderModule id={this.state.order_oid} renderedInModal />}
              footer={null}
              dialogClassName="w-11/12"
            />
          )}
          {this.state.showShipmentModal && (
            <CargomaticModal
              dataTestId="CargomaticModal-82d68cc1"
              className="w-full"
              show
              onHide={this.onHideShipmentModal}
              title={titleize(t('DETAIL', { count: 2 }))}
              body={<ShipmentModule modalID={this.state.shipment_oid} />}
              footer={null}
              location="top"
              dialogClassName="w-11/12"
            />
          )}
        </div>
        <SaveModal
          isOpen={this.state.showSaveModal}
          onClose={this.handleHideSaveReportModal}
          isNew={this.state.isSavingNewReport}
          onSave={this.handleSaveReport}
          report={this.state.report}
        />
        <DeleteReportModal
          isOpen={this.state.showConfirmDeleteReportModal}
          onClose={this.handleHideConfirmDeleteReportModal}
          onConfirm={this.handleConfirmDelete}
        />
      </Layout>
    );
  }
}

export default withTranslation()(
  withLocaleContext(withPageContext(withGroups(MasterList))),
);
